/**
 * AI Skin Scan ("סריקת עור AI") — shared types for the TimeIt Skin Journey feature.
 *
 * A scan captures a client's face, runs an AI (Gemini multimodal) skin assessment,
 * and stores per-concern scores + recommendations so a clinic can track progress
 * over time. These types are shared by the backend (analysis + persistence) and the
 * dashboard (capture UI + results), and are designed to be reused by the Phase-2
 * customer-app home self-scan flow.
 *
 * IMPORTANT: scores are on a 0–100 scale where HIGHER = HEALTHIER skin.
 * This is a cosmetic/aesthetic assessment only — never a medical diagnosis.
 */

/** The skin concerns assessed by a scan (the 14 industry-standard metrics below). */
export enum SkinConcernEnum {
  Hydration = 'hydration', // לחות
  Oiliness = 'oiliness', // שומניות
  Redness = 'redness', // אדמומיות
  Texture = 'texture', // מרקם
  Pores = 'pores', // נקבוביות
  Wrinkles = 'wrinkles', // קמטוטים / קווים דקים
  Pigmentation = 'pigmentation', // כתמי פיגמנטציה
  DarkCircles = 'dark_circles', // עיגולים שחורים
  Puffiness = 'puffiness', // נפיחות / שקיות מתחת לעיניים
  Firmness = 'firmness', // מוצקות / אלסטיות
  Evenness = 'evenness', // אחידות גוון
  Acne = 'acne', // אקנה / פצעונים
  Radiance = 'radiance', // זוהר / קורנות
  Sensitivity = 'sensitivity', // רגישות
}

/** Where the scan was captured. */
export enum SkinScanSourceEnum {
  Clinic = 'clinic', // captured by the cosmetician in the clinic (v1)
  Home = 'home', // self-captured by the customer at home (Phase 2)
}

/** Which angle a captured image represents. A session may hold several. */
export enum SkinImageDirectionEnum {
  Front = 'front', // חזית
  Left = 'left', // צד שמאל
  Right = 'right', // צד ימין
}

/** A single captured image within a scan session, tagged with its angle. */
export interface ISkinScanImage {
  url: string; // ImageKit URL
  fileId?: string; // ImageKit fileId, kept so the image can be deleted with the scan
  direction: SkinImageDirectionEnum;
}

/** Hebrew labels + canonical ordering for the capture directions. */
export const skinImageDirectionLabels: Record<SkinImageDirectionEnum, string> = {
  [SkinImageDirectionEnum.Front]: 'חזית',
  [SkinImageDirectionEnum.Left]: 'צד שמאל',
  [SkinImageDirectionEnum.Right]: 'צד ימין',
};

/** The directions in display order (front first — it is also the analysed image). */
export const skinImageDirectionOrder: SkinImageDirectionEnum[] = [
  SkinImageDirectionEnum.Front,
  SkinImageDirectionEnum.Left,
  SkinImageDirectionEnum.Right,
];

/** Default requested directions when a customer has none configured (single front image). */
export const defaultSkinScanDirections: SkinImageDirectionEnum[] = [SkinImageDirectionEnum.Front];

/** Severity of a concern (inverse of its score). */
export enum SkinConcernSeverityEnum {
  Low = 'low', // healthy / minor
  Moderate = 'moderate',
  High = 'high', // needs attention
}

/** Reasons an image can be rejected for analysis (client + server share this). */
export enum SkinScanQualityIssueEnum {
  NoFace = 'no_face',
  MultipleFaces = 'multiple_faces',
  NotAFace = 'not_a_face',
  TooDark = 'too_dark',
  TooBright = 'too_bright',
  Blurry = 'blurry',
  FaceTooFar = 'face_too_far',
  FaceTooClose = 'face_too_close',
  FaceNotCentered = 'face_not_centered',
  Obstructed = 'obstructed', // glasses / hair / mask / heavy makeup covering skin
  LowResolution = 'low_resolution',
}

/** A single quality problem with user-facing Hebrew guidance. */
export interface ISkinScanQualityIssue {
  type: SkinScanQualityIssueEnum;
  message: string; // Hebrew, shown to the operator
}

/** Result of a quality check (client-side live check or server-side gate). */
export interface ISkinScanQualityResult {
  usable: boolean;
  issues: ISkinScanQualityIssue[];
  guidance?: string; // overall Hebrew guidance, e.g. "התמונה מצוינת!" / "נסו שוב עם יותר אור"
}

/** A single assessed concern. */
export interface ISkinConcernScore {
  concern: SkinConcernEnum;
  label: string; // Hebrew label
  score: number; // 0–100, higher = healthier
  severity: SkinConcernSeverityEnum;
  description: string; // short Hebrew explanation of what the AI observed
}

/** A treatment/service the AI recommends, mapped to the business's catalogue when possible. */
export interface ISkinScanRecommendedService {
  serviceId?: string; // matches a `business.services[].id` when the AI could map it
  name: string; // Hebrew service / treatment name
  reason: string; // why it's recommended for this skin (Hebrew)
}

/** The recommendation block produced alongside an analysis. */
export interface ISkinScanRecommendations {
  recommendedServices: ISkinScanRecommendedService[];
  homeCareTips: string[]; // Hebrew bullet tips for between-visit care
  suggestedRescanDays?: number; // when to scan again for meaningful tracking
}

/** The raw AI analysis output (no persistence concerns). */
export interface ISkinAnalysisResult {
  usable: boolean; // false when the AI deems the image unsuitable for analysis
  unusableReason?: string; // Hebrew explanation when usable = false
  overallScore: number; // 0–100 overall skin-health score
  skinType?: string; // Hebrew: יבש / שמן / מעורב / רגיל / רגיש
  estimatedSkinAge?: number; // optional cosmetic estimate
  summary: string; // warm, non-medical Hebrew summary of the skin's condition
  concerns: ISkinConcernScore[];
  recommendations: ISkinScanRecommendations;
}

/** A stored scan record (business-scoped). Extends the analysis result with metadata. */
export interface ISkinScan extends ISkinAnalysisResult {
  _id?: string;
  businessId: string;
  customerId: string;
  customerName?: string;
  providerId?: string; // the cosmetician/employee who captured the scan
  appointmentId?: string; // optional link to an appointment
  treatmentSessionId?: string; // optional link to ICustomer.currentTreatmentSession
  imageUrl: string; // ImageKit URL of the primary (front) image — kept for back-compat with trend/list/shared views
  imageFileId?: string; // ImageKit fileId of the primary image
  /**
   * All images captured in this session, tagged by direction (front/left/right).
   * The AI analysis is run on the front image (or the first when no front exists).
   * Legacy scans have no `images`; consumers fall back to `imageUrl`.
   */
  images?: ISkinScanImage[];
  source: SkinScanSourceEnum;
  consentAcknowledged: boolean; // client consented to capture & storage
  notes?: string;
  tokensUsed?: number;
  createdAt?: Date | string;
  updatedAt?: Date | string;
}

/** Per-concern change between two scans. */
export interface ISkinConcernDelta {
  concern: SkinConcernEnum;
  label: string;
  previousScore: number;
  currentScore: number;
  delta: number; // currentScore - previousScore (positive = improvement)
  severity: SkinConcernSeverityEnum; // current severity
}

/** Before/after comparison of two scans. */
export interface ISkinScanComparison {
  previousScanId: string;
  currentScanId: string;
  previousDate: Date | string;
  currentDate: Date | string;
  overallDelta: number; // current overall - previous overall
  concernDeltas: ISkinConcernDelta[];
  summary: string; // AI-generated Hebrew progress narrative
}

/**
 * Payload to create a scan. Sent as multipart: one or more `images` files plus a
 * `directions` field — a JSON array of {@link SkinImageDirectionEnum} aligned to the
 * files by index — alongside the metadata fields below.
 */
export interface ICreateSkinScanRequest {
  customerId: string;
  source?: SkinScanSourceEnum;
  appointmentId?: string;
  treatmentSessionId?: string;
  consentAcknowledged: boolean;
  notes?: string;
  directions?: string; // JSON-encoded SkinImageDirectionEnum[], aligned to the uploaded files
}

/**
 * Customer-facing scan summary — images only, with NO AI data (no scores, concerns,
 * skin type, summary or recommendations). Used for the customer's own self-scan
 * history on the booking site, where analysis results are intentionally hidden.
 */
export interface ISkinScanPublicSummary {
  _id: string;
  imageUrl: string; // primary (front) image — drives the thumbnail
  images?: ISkinScanImage[]; // all directional images in the session (front still primary)
  createdAt: Date | string;
  source: SkinScanSourceEnum;
}

/** A single point on the shared "general change" graph (overall score) + its image. */
export interface ISkinProgressPoint {
  _id: string;
  imageUrl: string;
  createdAt: Date | string;
  overallScore: number;
}

/**
 * Read-only progress payload behind an admin-shared link: the general-change graph
 * (overall score over time) plus the image history. No per-concern detail or
 * recommendations are exposed.
 */
export interface ISkinProgressShared {
  /**
   * Whether the owner has opted this customer in to seeing their progress (the "share"
   * toggle). When false, the customer-facing page shows upload + photos but no graph/plan.
   * Undefined on the token-shared view (a valid token always implies enabled).
   */
  enabled?: boolean;
  customerName?: string;
  businessTitle?: string;
  /** The facial treatment plan the business owner filled for this customer. */
  treatmentPlan?: string;
  points: ISkinProgressPoint[];
}

/** Whether a customer's progress link is shared, and its (unguessable) token. */
export interface ISkinScanShareInfo {
  enabled: boolean;
  token?: string;
}

/**
 * Canonical ordering + Hebrew labels for the concerns, shared by the AI prompt
 * and the UI so the model and the dashboard always agree on the set & wording.
 */
export const skinConcernLabels: Record<SkinConcernEnum, string> = {
  [SkinConcernEnum.Hydration]: 'לחות',
  [SkinConcernEnum.Oiliness]: 'שומניות',
  [SkinConcernEnum.Redness]: 'אדמומיות',
  [SkinConcernEnum.Texture]: 'מרקם העור',
  [SkinConcernEnum.Pores]: 'נקבוביות',
  [SkinConcernEnum.Wrinkles]: 'קמטוטים וקווים דקים',
  [SkinConcernEnum.Pigmentation]: 'כתמי פיגמנטציה',
  [SkinConcernEnum.DarkCircles]: 'עיגולים שחורים',
  [SkinConcernEnum.Puffiness]: 'נפיחות מתחת לעיניים',
  [SkinConcernEnum.Firmness]: 'מוצקות ואלסטיות',
  [SkinConcernEnum.Evenness]: 'אחידות גוון',
  [SkinConcernEnum.Acne]: 'אקנה ופצעונים',
  [SkinConcernEnum.Radiance]: 'זוהר וקורנות',
  [SkinConcernEnum.Sensitivity]: 'רגישות',
};

/** The full concern set in display order. */
export const skinConcernOrder: SkinConcernEnum[] = [
  SkinConcernEnum.Hydration,
  SkinConcernEnum.Oiliness,
  SkinConcernEnum.Texture,
  SkinConcernEnum.Pores,
  SkinConcernEnum.Radiance,
  SkinConcernEnum.Evenness,
  SkinConcernEnum.Redness,
  SkinConcernEnum.Pigmentation,
  SkinConcernEnum.Wrinkles,
  SkinConcernEnum.Firmness,
  SkinConcernEnum.DarkCircles,
  SkinConcernEnum.Puffiness,
  SkinConcernEnum.Acne,
  SkinConcernEnum.Sensitivity,
];
